import React, { useEffect, useMemo, useState } from 'react';

import { Delete, PlayArrow } from '@mui/icons-material';
import { Box, Button, IconButton, Typography } from '@mui/material';

interface VideoPreviewProps {
  video?: string;
  baseUrl?: string | null;
  onDelete?: () => void;
  title?: string;
  aspectRatio?: string;
  maxWidth?: number | string;
}

const isAbsoluteUrl = (value: string) => /^https?:\/\//i.test(value);

export const VideoPreview: React.FC<VideoPreviewProps> = ({
  video,
  baseUrl,
  onDelete,
  title = 'Загруженное видео',
  aspectRatio = '16 / 9',
  maxWidth = '100%',
}) => {
  const [hovered, setHovered] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  const videoSrc = useMemo(() => {
    if (!video) {
      return '';
    }

    if (isAbsoluteUrl(video) || !baseUrl) {
      return video;
    }

    return `${baseUrl}${video}`;
  }, [baseUrl, video]);

  useEffect(() => {
    setIsLoaded(false);
  }, [videoSrc]);

  if (!videoSrc) {
    return null;
  }

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      <Typography variant="subtitle2">{title}</Typography>
      <Box
        position="relative"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        sx={{
          width: '100%',
          maxWidth,
          aspectRatio,
          borderRadius: '12px',
          overflow: 'hidden',
          background: '#111827',
          boxShadow: '0 8px 24px rgba(15, 23, 42, 0.18)',
        }}
      >
        {isLoaded ? (
          // eslint-disable-next-line jsx-a11y/media-has-caption
          <video
            src={videoSrc}
            controls
            controlsList="nodownload"
            preload="metadata"
            style={{
              display: 'block',
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              background: '#000',
            }}
          />
        ) : (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            gap={2}
            sx={{
              width: '100%',
              height: '100%',
              color: '#fff',
              background: 'linear-gradient(135deg, rgba(30,41,59,0.98), rgba(15,23,42,0.94))',
              textAlign: 'center',
              px: 3,
            }}
          >
            <Typography variant="body2" color="inherit">
              Видео загружено, но не подгружается автоматически.
            </Typography>
            <Button variant="contained" startIcon={<PlayArrow />} onClick={() => setIsLoaded(true)}>
              Показать видео
            </Button>
          </Box>
        )}

        {hovered && (
          <Box
            position="absolute"
            top={8}
            right={8}
            zIndex={1}
            sx={{
              borderRadius: '999px',
              backgroundColor: 'rgba(0, 0, 0, 0.52)',
            }}
          >
            <IconButton onClick={onDelete} sx={{ color: '#fff' }}>
              <Delete />
            </IconButton>
          </Box>
        )}
      </Box>
    </Box>
  );
};
